import type { NunjucksEnvironment, NunjucksLoader } from '../types/nunjucks-browser.ts';
import { toCamelCase, formatDate, formatNumero } from './utils.ts';
import { MODALITA_RACCOLTA_LABEL } from '@/types/entities/raccolta-delega.ts';
import { etichettaStato } from '@/types/entities/delega.ts';

// --- Costanti (allineate a web/boot/template-engine.ts) ---

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'] as const;

const STATO_BADGE: Record<string, string> = {
  // Stati deleghe
  CONFERMATA: 'success',
  DA_PRESENTARE: 'warning',
  PRESENTATA: 'info',
  RIFIUTATA: 'danger',
  // Stati lavoratore (V=vigente, T=trasferito, C=cessato, D=deceduto, P=pensionato)
  V: 'success',
  T: 'warning',
  C: 'danger',
  D: 'dark',
  P: 'secondary',
};
const STATO_LAVORATORE: Record<string, string> = {
  V: 'Attivo',
  T: 'Trasferito',
  C: 'Cessato',
  D: 'Deceduto',
  P: 'Pensionato',
};
const ICONA_STATO_LAVORATORE: Record<string, string> = {
  V: 'bi-person-check',
  T: 'bi-arrow-left-right',
  C: 'bi-person-x',
  D: 'bi-person-dash',
  P: 'bi-person-slash',
};
const STATO_DELEGA_BADGE: Record<string, string> = {
  BOZZA: 'secondary',
  DA_PRESENTARE: 'info',
  PRESENTATA: 'primary',
  CONFERMATA: 'success',
  ARCHIVIATA: 'danger',
};

const BADGE_CASSA: Record<string, string> = {
  CE: 'primary',
  EC: 'warning',
};
const NOME_CASSA: Record<string, string> = {
  CE: 'Cassa Edile',
  EC: 'Edilcassa',
};
const NOME_SINDACATO: Record<string, string> = {
  '9': 'NON ISCRITTO',
  '10': 'FILLEA',
  '20': 'FILCA',
  '22': 'FILCA',
  '30': 'FENEAL',
  '32': 'FILLEA',
};

// --- Template store ---

let templateResources: Record<string, string> = {};

export function setTemplates(views: Record<string, string>): void {
  templateResources = views;
}

// --- Custom loader ---

const loader: NunjucksLoader = {
  getSource(name: string) {
    const key = toCamelCase(name);
    const fromStore = templateResources[key];
    if (fromStore !== undefined) {
      return { src: fromStore, path: name, noCache: true };
    }

    const el = document.getElementById(name);
    if (el && (el.getAttribute('type') === 'text/html' || el.getAttribute('type') === 'text/nunjucks')) {
      return { src: el.innerHTML, path: name, noCache: true };
    }

    throw new Error(`Template "${name}" non trovato (key: "${key}")`);
  },
};

// --- Environment singleton ---

let _env: NunjucksEnvironment | null = null;

function env(): NunjucksEnvironment {
  if (_env) return _env;

  if (!window.nunjucks) {
    throw new Error('window.nunjucks non disponibile: caricare nunjucks-slim.min.js prima dell\'app');
  }

  _env = new window.nunjucks.Environment(loader);

  _env.addFilter('date', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    return formatDate(v);
  });

  _env.addFilter('periodo', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    let anno: string, mese: string;
    if (v.includes('_')) { anno = v.split('_')[0]!; mese = v.split('_')[1]!; }
    else if (v.includes('-')) { const p = v.split('-'); anno = p[0]!; mese = p[1]!; }
    else { anno = v.slice(0, 4); mese = v.slice(4, 6); }
    const idx = parseInt(mese, 10) - 1;
    return `${MESI[idx] ?? mese} ${anno}`;
  });

  _env.addFilter('stato_badge', (v: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    return STATO_BADGE[v] ?? 'secondary';
  });
  
  _env.addFilter('stato_lavoratore', (v: unknown) => {
    const fallback = `<span class="text-muted mx-1">&mdash;</span>`;
    if (!v || typeof v !== 'string' || v.trim() === '') return new window.nunjucks.runtime.SafeString(fallback);
    const key = v.trim();
    const label = STATO_LAVORATORE[key];
    if (!label) return new window.nunjucks.runtime.SafeString(fallback);
    const badge = STATO_BADGE[key] ?? 'secondary';
    const icon = ICONA_STATO_LAVORATORE[key] ?? 'bi-question-circle';
    const html = `<span class="text-${badge} mx-1" data-tooltip="${label}" data-position="top"><i class="bi ${icon}"></i></span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });
  
  _env.addFilter('icon_sindacato', (v: unknown) => {
    const fallback = `<span class="text-muted mx-1" data-tooltip="NON ISCRITTO">&mdash;</span>`;
    if (v === null || v === undefined || v === '') return new window.nunjucks.runtime.SafeString(fallback);
    const code = String(v).trim();
    if (!code || code === '9') return new window.nunjucks.runtime.SafeString(fallback);
    const nome = NOME_SINDACATO[code];
    if (!nome) return new window.nunjucks.runtime.SafeString(fallback);
    const html = `<span class="sindacato ${nome.toLowerCase()} mx-1" data-tooltip="${nome}" data-position="top"></span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });
  
  _env.addFilter('badge_cassa', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    const cls = BADGE_CASSA[v] ?? 'secondary';
    const html = `<span class="badge bg-${cls} mx-1" data-tooltip="${NOME_CASSA[v]||""}" data-position="top">${v}</span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });

  // Badge globale "Cessato": solo se cessato in entrambe le casse, con la cessazione più recente
  _env.addFilter('badge_cessato', (v: unknown) => {
    const pos = (v ?? {}) as Record<string, { stato?: string; dataCessazione?: string | null } | undefined>;
    if (pos.CE?.stato !== 'C' || pos.EC?.stato !== 'C') return '';
    const recente = [pos.CE.dataCessazione, pos.EC.dataCessazione].filter(Boolean).sort().pop();
    const data = recente ? ` <small>${formatDate(recente)}</small>` : '';
    return new window.nunjucks.runtime.SafeString(`<span class="badge bg-danger bg-opacity-75 small ms-2">Cessato${data}</span>`);
  });

  _env.addFilter('modalita_raccolta', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    return MODALITA_RACCOLTA_LABEL[v as keyof typeof MODALITA_RACCOLTA_LABEL] ?? v;
  });

  _env.addFilter('json', (v: unknown) => {
    return JSON.stringify(v, null, 2);
  });

  _env.addFilter('stato_delega_badge', (v: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    return STATO_DELEGA_BADGE[v] ?? 'secondary';
  });

  _env.addFilter('nome_sindacato', (v: unknown) => {
    if (!v) return '';
    const code = String(v);
    return NOME_SINDACATO[code] ?? code;
  });

  _env.addFilter('stato_label', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    return etichettaStato(v);
  });

  _env.addFilter('numero', (v: unknown) => formatNumero(v));

  _env.addFilter('badge_class', (v: unknown, filterName: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    const name = typeof filterName === 'string' ? filterName : 'stato_badge';
    const map = name === 'stato_delega_badge' ? STATO_DELEGA_BADGE : STATO_BADGE;
    return map[v] ?? 'secondary';
  });

  _env.addFilter('badge_text', (v: unknown, filterName: unknown) => {
    if (!v || typeof v !== 'string') return '';
    const name = typeof filterName === 'string' ? filterName : 'stato_label';
    if (name === 'nome_sindacato') return NOME_SINDACATO[v] ?? v;
    return etichettaStato(v);
  });

  return _env;
}

// --- API pubblica ---

export function templateParse(templateId: string, data: Record<string, unknown>): string | null {
  try {
    return env().renderString(loader.getSource(templateId).src, data);
  } catch {
    console.error(`templateParse: errore rendering "${templateId}"`);
    return null;
  }
}

export function templateParseToElement(
  templateId: string,
  target: HTMLElement,
  data: Record<string, unknown>,
): boolean {
  const html = templateParse(templateId, data);
  if (html === null) return false;
  target.innerHTML = html;
  return true;
}

export function templateRenderString(templateString: string, data: Record<string, unknown>): string {
  return env().renderString(templateString, data);
}
